// Multi-camera synchronized playback core (APP#43 — 共用時鐘 scrubber).
//
// One shared, time-absolute clock (µs epoch) drives N cameras. At any clock position
// each camera independently resolves to "the clip covering that instant + a seek offset"
// or "no recording (gap)". Seeking/playing/pausing the shared clock therefore fans out
// to every camera; per-camera coverage gaps show as blanks on that camera only.
//
// These are the PURE, DOM-free primitives (unit-tested in multicam.test.ts); the React
// glue in MultiPlaybackPage.tsx wires them to <video> elements. Fraction↔µs mapping and
// segment coalescing are reused from ./timeline (the same core the single-cam page uses),
// so the shared scrubber renders identically — this file only adds the multi-clip layer.

import { coalesceSegments, type ClipSpan, type Segment, type TimeRange } from './timeline';

/**
 * Index of the clip in a per-camera list that COVERS `us` (clip_start ≤ us ≤ clip_end),
 * or null when `us` falls in a gap / outside all clips. `clips` MUST be sorted ascending
 * by clip_start_ts and be non-overlapping (as day recordings are, spec 17 §2.1) — binary
 * search relies on it. O(log n) so a 5,760-clip day resolves per animation tick cheaply.
 */
export function activeClipIndexAt(clips: ClipSpan[], us: number): number | null {
  let lo = 0;
  let hi = clips.length - 1;
  let cand = -1; // last clip whose start ≤ us
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (clips[mid].clip_start_ts <= us) {
      cand = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (cand < 0) return null;
  return us <= clips[cand].clip_end_ts ? cand : null;
}

/**
 * Seconds offset of `us` inside `clip` — the value to assign to `<video>.currentTime`.
 * Clamped to [0, clip duration]; a `us` before the clip start yields 0, after the end
 * yields the clip length (so a marginally-out-of-range clock never seeks negative/NaN).
 */
export function clipOffsetSec(clip: ClipSpan, us: number): number {
  const clamped = Math.min(clip.clip_end_ts, Math.max(clip.clip_start_ts, us));
  return (clamped - clip.clip_start_ts) / 1_000_000;
}

/** One camera's resolution of the shared clock: which clip to show and where to seek it. */
export interface CamSyncTarget {
  /** Index into that camera's clip list, or null when the camera has no recording now (gap). */
  clipIdx: number | null;
  /** Seek offset (seconds) within the clip; 0 when in a gap. */
  offsetSec: number;
}

/**
 * Resolve EVERY camera against one shared clock position. Order matches `perCamClips`.
 * The clock is absolute time — cameras with different segment boundaries each land on
 * their own covering clip (or a gap), which is exactly the sync invariant.
 */
export function syncTargets(perCamClips: ClipSpan[][], us: number): CamSyncTarget[] {
  return perCamClips.map((clips) => {
    const idx = activeClipIndexAt(clips, us);
    return idx === null ? { clipIdx: null, offsetSec: 0 } : { clipIdx: idx, offsetSec: clipOffsetSec(clips[idx], us) };
  });
}

/** True when a clock position is a gap for ALL selected cameras (dead air — nothing to show). */
export function allInGap(targets: CamSyncTarget[]): boolean {
  return targets.length > 0 && targets.every((t) => t.clipIdx === null);
}

/**
 * Earliest clip start strictly AFTER `us` across all cameras, or null if none.
 * Used to skip a shared gap forward to the next instant any camera has footage
 * (mirrors the single-cam "跳至下一段" behaviour, spec 17 §4.3).
 */
export function earliestNextClipStart(perCamClips: ClipSpan[][], us: number): number | null {
  let best: number | null = null;
  for (const clips of perCamClips) {
    for (const c of clips) {
      // clips are ascending → the first start past `us` is that camera's nearest next.
      if (c.clip_start_ts > us) {
        if (best === null || c.clip_start_ts < best) best = c.clip_start_ts;
        break;
      }
    }
  }
  return best;
}

/**
 * Union coverage across all cameras, coalesced into display segments for the SHARED
 * scrubber track (any camera recording ⇒ the shared timeline shows footage there).
 * Per-camera lanes render their own {@link coalesceSegments} for per-gap detail.
 */
export function unionSegments(perCamClips: ClipSpan[][], range: TimeRange, gapUs?: number): Segment[] {
  return coalesceSegments(perCamClips.flat(), range, gapUs);
}

/** Result of advancing the shared clock by one tick. */
export interface ClockStep {
  /** New clock position (µs), clamped to the window. */
  us: number;
  /** True when the clock reached the window end and playback should stop. */
  ended: boolean;
}

/**
 * Advance the shared clock by real elapsed wall-time × playback rate. The clock is the
 * single source of truth for time — videos are slaves kept aligned by {@link driftExceeds}
 * — so a stalled/buffering camera never drags the whole group's timeline backwards.
 * Clamps to `range.toUs` and reports `ended` there.
 */
export function advanceClock(us: number, elapsedMs: number, rate: number, range: TimeRange): ClockStep {
  const next = us + Math.max(0, elapsedMs) * 1000 * rate; // ms → µs, × speed
  if (next >= range.toUs) return { us: range.toUs, ended: true };
  return { us: next, ended: false };
}

/**
 * Whether a slave video has drifted from the shared clock beyond tolerance and must be
 * re-seeked. Playing tolerates more drift (decode jitter shouldn't cause constant
 * re-seeks); a paused/scrub correction uses a tight tolerance for frame-accurate parity.
 */
export function driftExceeds(currentTimeSec: number, expectedOffsetSec: number, thresholdSec = 0.75): boolean {
  return Math.abs(currentTimeSec - expectedOffsetSec) > thresholdSec;
}
